import { Container, Typography } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router';

import Auth from '../../../../api/auth';
import Provision from '../../../../api/provision';
import Accordion from '../../../../components/Accordion/Accordion';
import SuccessModal from '../../../../components/Modal/SuccessModal';
import useApiQuery from '../../../../hooks/useApiQuery';

import EvaluationForm from './EvaluationForm';

import { useUserData } from '@/redux/selectors';
import type { APIHookArg } from '@/types';
import type { VirtualCloud } from '@/types/cloudTypes';
import type { Product } from '@/types/productTypes';

const trialQuota = {
  compute: { cores: 10, ram: 1024 },
  network: { floatingIps: 1 },
  volume: { gigabytes: 10, objectsMegabytes: 0 },
};

const Description = (): React.JSX.Element => {
  const intl = useIntl();

  return (
    <>
      <Typography gutterBottom>
        {intl.formatMessage({ id: 'evaluationDescriptionIntro' })}
      </Typography>
      <Typography gutterBottom>{intl.formatMessage({ id: 'evaluationDescriptionGtc' })}</Typography>
      <Typography gutterBottom>
        {intl.formatMessage({ id: 'evaluationDescriptionProductSpec' })}
      </Typography>
      <Typography gutterBottom>{intl.formatMessage({ id: 'evaluationDescriptionSla' })}</Typography>
    </>
  );
};

interface EvaluationFormValues {
  osProjectName: string;
}

const Evaluation = (): React.JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const userData = useUserData();

  const [trialProduct, setTrialProduct] = useState<Product | null>(null);
  const [virtualCloud, setVirtualCloud] = useState<VirtualCloud>({
    id: '',
    projects: [],
    allowed_quota: {
      compute: { cores: 0, ram: 0 },
      volume: {
        gigabytes: 0,
        objectsMegabytes: 0,
        objects_megabytes: 0,
      },
      network: {
        floatingIps: 0,
        floating_ips: 0,
      },
    },
    used_quota: {
      compute: { cores: 0, ram: 0 },
      volume: {
        gigabytes: 0,
        objectsMegabytes: 0,
        objects_megabytes: 0,
      },
      network: {
        floatingIps: 0,
        floating_ips: 0,
      },
    },
    unusedQuota: {
      compute: { cores: 0, ram: 0 },
      volume: { gigabytes: 0, objectsMegabytes: 0 },
      network: { floatingIps: 0 },
    },
    cloud: { name: '' },
  });
  const [osProjectOrdered, setOsProjectOrdered] = useState<boolean>(false);

  const { makeApiCall: fetchProducts } = useApiQuery(Auth.fetchV2Products as unknown as APIHookArg);
  const { makeApiCall: getVirtualCloudsCall } = useApiQuery(
    Provision.fetchVirtualClouds as unknown as APIHookArg
  );
  const { makeApiCall: createOsProject } = useApiQuery(
    Provision.createOsProject as unknown as APIHookArg
  );

  const trialYearRevoked = useMemo(() => {
    const oneYearMs = 365 * 24 * 3600 * 1000;
    return userData.organization?.createdAt
      ? new Date().getTime() - new Date(userData.organization.createdAt).getTime() > oneYearMs
      : false;
  }, [userData.organization?.createdAt]);

  const trialEnabled = useMemo(
    () => userData.organization?.marketplaceItems?.some(item => item.name === 'Evaluation'),
    [userData.organization?.marketplaceItems]
  );

  useEffect(() => {
    if (trialYearRevoked || !trialEnabled) return;

    const getTrialProduct = async (): Promise<void> => {
      const response = await fetchProducts({ types: ['evaluation'] });
      const data = (
        response && typeof response === 'object' && 'docs' in response
          ? (response as { docs: Product[] }).docs
          : response
      ) as Product[];
      if (data.length && data[0]) {
        setTrialProduct(data[0]);
      }
    };

    getTrialProduct();
  }, [trialYearRevoked, trialEnabled, fetchProducts]);

  useEffect(() => {
    if (!trialProduct) return;

    const getTrialVirtualCloud = async (): Promise<void> => {
      const { juiceGroup } = trialProduct.details;
      const data = (await getVirtualCloudsCall({ juiceGroups: [juiceGroup] })) as {
        cloud: { name: string };
        projects: unknown[];
      }[];

      if (data.length > 0) {
        setVirtualCloud(
          prev =>
            ({
              ...prev,
              id: data[0]?.cloud.name ?? '',
              projects: data[0]?.projects,
            }) as VirtualCloud
        );
      }
    };

    getTrialVirtualCloud();
  }, [trialProduct, getVirtualCloudsCall]);

  const handleSubmit = async (values: EvaluationFormValues): Promise<void> => {
    const data = await createOsProject({
      osProjectName: values.osProjectName,
      cloudId: virtualCloud.id,
      type: 'computeTrial',
      juiceGroup: trialProduct?.details.juiceGroup,
      projectId: trialProduct?._id,
      productId: trialProduct?._id,
      organizationId: userData.organizationId,
      requestedQuota: trialQuota,
    });

    if (data) setOsProjectOrdered(true);
  };

  const renderEvaluationDetails = (): React.JSX.Element => {
    if (trialYearRevoked) {
      return <Typography>{intl.formatMessage({ id: 'evaluationExpired' })}</Typography>;
    }
    if (!trialEnabled) {
      return <Typography>{intl.formatMessage({ id: 'evaluationNotEnabled' })}</Typography>;
    }
    if (!trialProduct) {
      return <Typography>{intl.formatMessage({ id: 'evaluationNotAssigned' })}</Typography>;
    }

    return virtualCloud.projects?.length ? (
      <>
        <Typography display='inline'>
          {intl.formatMessage({ id: 'evaluationAllocatedToTrialProduct' })}
        </Typography>
        <Typography
          variant='h6'
          display='inline'
        >
          {' '}
          {trialProduct.name}
        </Typography>
        <Typography gutterBottom>
          {intl.formatMessage({ id: 'evaluationNavigateToProducts' })}{' '}
          <Link to='/products/compute'>
            <Typography
              component='span'
              display='inline'
            >
              {intl.formatMessage({ id: 'evaluationClickHere' })}
            </Typography>
          </Link>
        </Typography>
      </>
    ) : (
      <>
        <Typography display='inline'>
          {intl.formatMessage({ id: 'evaluationWillBeAllocatedToTrialProduct' })}
        </Typography>
        <Typography
          variant='h6'
          display='inline'
        >
          {' '}
          {trialProduct.name}
        </Typography>
      </>
    );
  };

  return (
    <Container maxWidth='md'>
      {osProjectOrdered && (
        <SuccessModal
          title={intl.formatMessage({ id: 'evaluationOsProjectOrdered' })}
          messageId='createOsProjectFulfilled'
          onSubmit={() => {
            navigate('/products/compute');
          }}
          onClose={() => {
            navigate('/products/compute');
          }}
        />
      )}
      <Typography
        variant='h4'
        color='primary'
        display='inline'
      >
        {intl.formatMessage({ id: 'evaluationProductTitle' })}
      </Typography>
      <Typography
        variant='h5'
        display='inline'
      >
        {' '}
        {intl.formatMessage({ id: 'evaluationFreeOfCharge' })}
      </Typography>
      <Accordion
        expanded
        title={intl.formatMessage({ id: 'projectDescriptionTitle' })}
        details={<Description />}
      />
      <br />
      {renderEvaluationDetails()}
      <Typography>{intl.formatMessage({ id: 'evaluationDefineOsProjectName' })}</Typography>
      <EvaluationForm
        disabled={
          !trialProduct ||
          trialYearRevoked ||
          !trialEnabled ||
          (virtualCloud.projects?.length ?? 0) > 0
        }
        onSubmit={handleSubmit}
      />
    </Container>
  );
};

export default Evaluation;
